import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import './TiltCard.css';

/**
 * TiltCard — a card wrapper that:
 *   1) reveals on scroll (fade + rise),
 *   2) tilts in 3D toward the pointer,
 *   3) renders a soft gradient glow that tracks the pointer.
 *
 * Drop-in replacement for a <Reveal className="x-card"> wrapper: pass the
 * card's own class via `className` so it stays the direct grid child and
 * layout is unchanged.
 *
 * Props:
 *  - className: the card's own class (e.g. "creator-card")
 *  - delay: scroll-reveal delay (s)
 *  - max: max tilt in degrees (default 8)
 *  - glow: render the cursor-tracking glow (default true)
 */
const TiltCard = ({ children, className = '', delay = 0, max = 8, glow = true, ...rest }) => {
  const ref = useRef(null);
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const sx = useSpring(px, { stiffness: 200, damping: 20 });
  const sy = useSpring(py, { stiffness: 200, damping: 20 });

  const rotateX = useTransform(sy, [0, 1], [max, -max]);
  const rotateY = useTransform(sx, [0, 1], [-max, max]);
  const glowX = useTransform(sx, (v) => `${v * 100}%`);
  const glowY = useTransform(sy, (v) => `${v * 100}%`);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
  };

  const handleLeave = () => {
    px.set(0.5);
    py.set(0.5);
  };

  return (
    <motion.div
      ref={ref}
      className={`tilt-card ${className}`}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{ rotateX, rotateY, transformPerspective: 900 }}
      {...rest}
    >
      {glow && (
        <motion.span
          className="tilt-card__glow"
          style={{ left: glowX, top: glowY }}
          aria-hidden="true"
        />
      )}
      {children}
    </motion.div>
  );
};

export default TiltCard;
